import { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { Target, TrendingUp, TrendingDown, CheckCircle2 } from "lucide-react";

const STATUS = {
  "Target Exceeded": { color: "#2E8B57", bg: "#E7F5EC", border: "#C7E9D3", Icon: CheckCircle2 },
  "Target Achieved": { color: "#2E8B57", bg: "#E7F5EC", border: "#C7E9D3", Icon: CheckCircle2 },
  "Ahead of Target": { color: "#003DA5", bg: "#E5EEFB", border: "#BFCFE8", Icon: TrendingUp },
  "On Track":        { color: "#003DA5", bg: "#F3F7FC", border: "#BFCFE8", Icon: Target },
  "Behind Target":   { color: "#D9534F", bg: "#FDECEA", border: "#F5B5B3", Icon: TrendingDown },
  "No Target Set":   { color: "#6B7280", bg: "#F1F5F9", border: "#E2E8F0", Icon: Target },
};

export function TargetProgressBar({ progress }) {
  if (!progress) return null;
  const p = progress;
  const pct = Math.min(p.achievement_pct, 200);
  const paceMarker = p.total_selling_days > 0 ? (p.elapsed_selling_days / p.total_selling_days) * 100 : 0;
  const st = STATUS[p.status] || STATUS["On Track"];
  const Icon = st.Icon;
  const barColor = p.achievement_pct >= 100 ? "#2E8B57" : p.status === "Behind Target" ? "#D9534F" : "#003DA5";

  return (
    <div className="space-y-4" data-testid="target-progress">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">{p.target_name || "Sales Target"}</div>
          <div className="mt-1 text-3xl font-heading font-bold text-[#001F54]" data-testid="target-current-sales">{money(p.current_sales)}</div>
          <div className="text-xs text-slate-500">of <span className="font-semibold text-slate-700">{money(p.target_value)}</span> target</div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border`} style={{ color: st.color, background: st.bg, borderColor: st.border }} data-testid="target-status">
          <Icon className="w-3.5 h-3.5" /> {p.status}
        </div>
      </div>

      <div className="relative h-3 bg-slate-100 rounded-full overflow-visible" data-testid="target-progress-bar">
        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-500"
             style={{ width: `${Math.min(pct, 100)}%`, background: barColor }} />
        {pct > 100 && (
          <div className="absolute inset-y-0 left-full h-full flex items-center">
            <div className="ml-0.5 h-3 rounded-full bg-[#2E8B57]" style={{ width: `${Math.min(pct - 100, 100) * 0.3}%` }} />
          </div>
        )}
        {paceMarker > 0 && paceMarker < 100 && (
          <div className="absolute top-[-4px] bottom-[-4px] w-0.5 bg-[#FFB547]" style={{ left: `${paceMarker}%` }} title="Expected pace">
            <div className="absolute top-full mt-1 -translate-x-1/2 text-[9px] font-bold text-[#8A5A00] whitespace-nowrap">pace</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <Metric label="Achievement" value={`${p.achievement_pct}%`} testid="metric-achievement" />
        <Metric label="Remaining" value={money(p.remaining)} testid="metric-remaining" />
        <Metric label="Days Remaining" value={p.remaining_selling_days} testid="metric-days-remaining" />
        <Metric label="Required / Day" value={money(p.required_per_day)} testid="metric-required-per-day" />
        <Metric label="Projected Month-End" value={money(p.projected_sales)} testid="metric-projected" />
        <Metric label="Projected Achievement" value={`${p.projected_achievement_pct}%`} testid="metric-projected-pct" />
        <Metric label="Pace Expected" value={money(p.pace_expected)} testid="metric-pace" />
        <Metric
          label="Ahead / Behind Pace"
          value={`${p.ahead_behind >= 0 ? "+" : ""}${money(p.ahead_behind)}`}
          tone={p.ahead_behind >= 0 ? "good" : "bad"}
          testid="metric-ahead-behind"
        />
      </div>
    </div>
  );
}

const Metric = ({ label, value, tone = "", testid }) => (
  <div className="bg-[#F3F7FC] rounded-lg border border-slate-200 p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{label}</div>
    <div className={`text-lg font-heading font-bold mt-0.5 ${tone === "good" ? "text-emerald-600" : tone === "bad" ? "text-red-600" : "text-[#001F54]"}`}>{value}</div>
  </div>
);

export function TargetSummaryCard({ apiPath = "/targets/summary", title = "Sales Target" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(apiPath).then(r => setData(r.data)).finally(() => setLoading(false));
  }, [apiPath]);
  if (loading) return <div className="bg-white rounded-xl border border-slate-200 p-5 text-slate-500 text-sm">Loading target…</div>;
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="no-target-card">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-100 grid place-items-center"><Target className="w-5 h-5 text-slate-400" /></div>
          <div>
            <div className="font-heading font-semibold">No active target for this period</div>
            <div className="text-xs text-slate-500">Ask your manager to set one.</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {data.map(p => (
        <div key={p.target_id} className="bg-white rounded-xl border border-slate-200 p-5 card-hoverable">
          <TargetProgressBar progress={p} />
        </div>
      ))}
    </div>
  );
}
